import { useMemo } from 'react';
import { useFormik } from 'formik';
// mantine
import { Button, UnstyledButton } from '@mantine/core';
// components
import { processFormData } from '@newo/toolkit/bff';
import type { ISettingPreset, ISetting } from '@newo/api/gateway';
import SettingField from '@/modules/Integrations/components/SettingField';
import ConnectorGroup from '@/pages/Integrations/components/ConnectorGroup';
// utils
import {
    buildFormFromPreset,
    isSettingVisible,
    pickVisibleSettings
} from '@/pages/Integrations/Integrations.utils.ts';

interface SettingsFormProps {
    presets: ISettingPreset[];
    settings?: ISetting[];
    onSubmit: (values: Record<string, unknown>) => Promise<unknown>;
    onCancel?: () => void;
}

type GroupData = {
    group: string;
    values: ISettingPreset[];
};

const SettingsForm = ({
    presets,
    settings,
    onCancel,
    onSubmit
}: SettingsFormProps) => {
    const { initialValues, validationSchema } = buildFormFromPreset(
        presets,
        settings
    );
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit(formData) {
            const data = processFormData(
                pickVisibleSettings(presets, formData)
            );
            // TODO: refactor
            // eslint-disable-next-line promise/catch-or-return
            onSubmit(data).finally(() => {
                formik.setSubmitting(false);
            });
        }
    });

    const notGroupedSettingsList = useMemo(() => {
        return presets.filter(
            (item) => !item.group && isSettingVisible(item, formik.values)
        );
    }, [presets, formik.values]);

    const groupedSettingsList = useMemo<GroupData[]>(() => {
        const groupedMap = new Map<string, ISettingPreset[]>();

        for (const item of presets) {
            if (!item.group || !isSettingVisible(item, formik.values)) continue;

            const list = groupedMap.get(item.group);
            if (list) {
                list.push(item);
            } else {
                groupedMap.set(item.group, [item]);
            }
        }

        return Array.from(groupedMap.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([group, values]) => ({ group, values }));
    }, [presets, formik.values]);

    return (
        <form
            autoComplete="off"
            className="flex flex-col gap-3"
            onSubmit={formik.handleSubmit}
        >
            {notGroupedSettingsList.map((control) => (
                <SettingField
                    control={control}
                    error={formik.errors[control.idn]}
                    key={control.idn}
                    value={formik.values[control.idn] as string}
                    onChange={(value) => {
                        formik.setFieldValue(control.idn, value);
                    }}
                />
            ))}

            <div className="flex flex-col gap-2">
                {groupedSettingsList.map((groupData) => {
                    return (
                        <ConnectorGroup
                            formik={formik}
                            groupData={groupData}
                            key={groupData.group}
                        />
                    );
                })}
            </div>
            <div className="flex justify-between gap-2">
                {onCancel ? (
                    <UnstyledButton
                        className="text-xs text-gray-6"
                        onClick={onCancel}
                    >
                        Cancel
                    </UnstyledButton>
                ) : undefined}
                <Button
                    className="ml-auto"
                    disabled={
                        !formik.dirty || !formik.isValid || formik.isSubmitting
                    }
                    size="xs"
                    type="submit"
                >
                    Save
                </Button>
            </div>
        </form>
    );
};

SettingsForm.displayName = 'SettingsForm';

export default SettingsForm;
